import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../api';
import { TopBar } from '../components/TopBar';
import type { Race } from '../types';
import { useMe } from './queries';

const statusChip: Record<Race['status'], string> = { draft: 'neutral', live: 'ok', closed: 'info' };

export function RacesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const races = useQuery({ queryKey: ['races'], queryFn: () => api<{ races: Race[] }>('/races') });
  const admin = useMe().data?.admin ?? false;
  const [whose, setWhose] = useState<'all' | 'mine'>('all');
  const shown = (races.data?.races ?? []).filter((r) => !admin || whose === 'all' || r.mine);
  const [name, setName] = useState('');
  const [raceDate, setRaceDate] = useState(new Date().toISOString().slice(0, 10));
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const create = useMutation({
    mutationFn: () => api<{ race: Race }>('/races', { body: { name, raceDate, timezone } }),
    onSuccess: ({ race }) => {
      qc.invalidateQueries({ queryKey: ['races'] });
      navigate(`/races/${race.id}/course`);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };

  return (
    <>
      <TopBar />
      <main className="page">
        {races.data && races.data.races.length === 0 && (
          <section className="card stack" style={{ gap: 10, borderLeft: '4px solid var(--accent)' }}>
            <h2 style={{ margin: 0 }}>Welcome to Splitline</h2>
            <p style={{ margin: 0 }}>Five steps to your first timed race. The guide covers each one in detail.</p>
            <ol className="help-list">
              <li>
                Create the race on the right, then add a <b>finish</b> timing point (and a start, if you want net times).
              </li>
              <li>
                <b>Import your roster</b>: the participant export from your registration site, as CSV or Excel. <Link to="/docs/roster">Roster formats</Link>
              </li>
              <li>
                <b>Add a station per phone</b>: a timing phone and a bib phone at the finish. <Link to="/docs/planning">How many phones?</Link>
              </li>
              <li>
                Ask volunteers to run the <b>device check</b> at {window.location.origin}/station/check, and print the <Link to="/help">volunteer card</Link>.
              </li>
              <li>
                <b>Rehearse</b> in a car park a few days before: walk across the line with a bib on and watch the times arrive. <Link to="/docs/rehearsal">Rehearsal checklist</Link>
              </li>
            </ol>
          </section>
        )}
        <div className="page-head">
          <div>
            <h1>{admin ? 'All races' : 'Your races'}</h1>
            <p>Set up a race, import its roster, add timing points and pair a phone at each one.</p>
          </div>
        </div>
        <div className="row">
          <section className="card flush main-col" style={{ gap: 0 }}>
            <div className="card-head">
              <h2 style={{ marginRight: 'auto' }}>Races</h2>
              {admin && (
                <select className="input" style={{ width: 'auto' }} value={whose} onChange={(e) => setWhose(e.target.value as typeof whose)} aria-label="Whose races">
                  <option value="all">Every director’s</option>
                  <option value="mine">Mine</option>
                </select>
              )}
            </div>
            {races.isLoading && <p className="empty">Loading…</p>}
            {races.data && shown.length === 0 && <p className="empty">No races yet. Create your first one.</p>}
            {shown.length > 0 && (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Race</th>
                      <th>Date</th>
                      {(admin || shown.some((r) => !r.mine)) && <th>Director</th>}
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <Link to={`/races/${r.id}`} style={{ fontWeight: 600 }}>
                            {r.name}
                          </Link>
                        </td>
                        <td className="mono">{r.raceDate}</td>
                        {(admin || shown.some((x) => !x.mine)) && <td className="small">{r.mine ? 'You' : `${r.owner ?? '—'}${admin ? '' : ' · you’re helping'}`}</td>}
                        <td>
                          <span className={`chip ${statusChip[r.status]}`}>{r.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          <form className="card side-col stack" onSubmit={submit} style={{ gap: 14 }}>
            <h2>New race</h2>
            <label className="field">
              Name
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Riverside 10K" required />
            </label>
            <label className="field">
              Date
              <input className="input" type="date" value={raceDate} onChange={(e) => setRaceDate(e.target.value)} required />
            </label>
            <div className="small muted">Time zone: {timezone}</div>
            {create.error && <div className="notice bad">{(create.error as Error).message}</div>}
            <button className="btn primary" type="submit" disabled={create.isPending}>
              Create race
            </button>
          </form>
        </div>
      </main>
    </>
  );
}
